/**
 * KiCockpitSection — Zentrales KI-Cockpit für Admin
 * Vereint: Briefing, Roadmap, Changelog, Zugangsdaten, Marken, Datenbank, Notizen, Fragen
 * 
 * Feedback-Loops: Admin schreibt Notizen/Anweisungen, KI liest und setzt um,
 * KI schreibt Feedback/Fragen, Admin antwortet.
 */

import { useState, useMemo } from 'react';
import {
  Cpu, BookOpen, Map, Clock, Shield, Globe, Database,
  MessageSquare, HelpCircle, Plus, Edit2, Trash2, X,
  ChevronDown, ChevronRight, Save, Archive, Eye, EyeOff,
  Copy, ExternalLink, AlertCircle, CheckCircle2, Loader2,
  FileText, Zap, Tag, Send, CornerDownRight,
} from 'lucide-react';
import { useKiCockpit, type CockpitBereich, type KiCockpitEintrag } from '@/hooks/useKiCockpit';
import { useZugangsdaten, useMarkenConfig } from '@/hooks/useAdminSettings';
import { toast } from 'sonner';

// ─── Bereich-Konfiguration ─────────────────────────────────────────────────

const BEREICH_CONFIG: Record<CockpitBereich, { icon: any; farbe: string; bgFarbe: string }> = {
  briefing: { icon: BookOpen, farbe: 'text-blue-600', bgFarbe: 'bg-blue-50' },
  roadmap: { icon: Map, farbe: 'text-amber-600', bgFarbe: 'bg-amber-50' },
  changelog: { icon: Clock, farbe: 'text-green-600', bgFarbe: 'bg-green-50' },
  zugangsdaten: { icon: Shield, farbe: 'text-red-600', bgFarbe: 'bg-red-50' },
  marken: { icon: Globe, farbe: 'text-purple-600', bgFarbe: 'bg-purple-50' },
  datenbank: { icon: Database, farbe: 'text-teal-600', bgFarbe: 'bg-teal-50' },
  notizen: { icon: MessageSquare, farbe: 'text-indigo-600', bgFarbe: 'bg-indigo-50' },
  fragen: { icon: HelpCircle, farbe: 'text-orange-600', bgFarbe: 'bg-orange-50' },
};

// ─── Eintrag-Karte ─────────────────────────────────────────────────────────

function EintragCard({ eintrag, onEdit, onDelete }: {
  eintrag: KiCockpitEintrag;
  onEdit: (e: KiCockpitEintrag) => void;
  onDelete: (id: string) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const isKi = eintrag.erstellt_von === 'ki_agent';
  const isSystem = eintrag.erstellt_von === 'system';

  return (
    <div className={`border rounded-lg p-3 mb-2 transition-colors ${
      isKi ? 'border-purple-200 bg-purple-50/30' : 
      isSystem ? 'border-gray-200 bg-gray-50/30' :
      'border-gray-200 bg-white'
    }`}>
      <div className="flex items-start gap-2">
        <button onClick={() => setExpanded(!expanded)} className="mt-0.5 flex-shrink-0">
          {expanded ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium text-foreground">{eintrag.titel}</span>
            <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500 font-medium">{eintrag.typ}</span>
            {isKi && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-purple-100 text-purple-600 font-medium flex items-center gap-0.5"><Cpu className="w-2.5 h-2.5" /> KI</span>}
            {eintrag.status === 'entwurf' && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-yellow-100 text-yellow-600 font-medium">Entwurf</span>}
          </div>
          {!expanded && eintrag.inhalt && (
            <p className="text-xs text-gray-400 mt-0.5 truncate">{eintrag.inhalt.substring(0, 120)}...</p>
          )}
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <button onClick={() => onEdit(eintrag)} className="p-1 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600">
            <Edit2 className="w-3.5 h-3.5" />
          </button>
          <button onClick={() => onDelete(eintrag.id)} className="p-1 rounded hover:bg-red-50 text-gray-400 hover:text-red-500">
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
      {expanded && eintrag.inhalt && (
        <div className="mt-2 ml-6 text-xs text-gray-600 whitespace-pre-wrap leading-relaxed border-t border-gray-100 pt-2">
          {eintrag.inhalt}
        </div>
      )}
      <div className="mt-1 ml-6 text-[10px] text-gray-300">
        {new Date(eintrag.erstellt_am).toLocaleDateString('de-DE')} · {eintrag.erstellt_von}
      </div>
    </div>
  );
}

// ─── Neuer Eintrag Form ────────────────────────────────────────────────────

function NeuerEintragForm({ bereichId, onSave, onCancel, editEintrag }: {
  bereichId: string;
  onSave: (data: any) => Promise<boolean>;
  onCancel: () => void;
  editEintrag?: KiCockpitEintrag | null;
}) {
  const [titel, setTitel] = useState(editEintrag?.titel || '');
  const [typ, setTyp] = useState(editEintrag?.typ || 'notiz');
  const [inhalt, setInhalt] = useState(editEintrag?.inhalt || '');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!titel.trim()) { toast.error('Titel ist erforderlich'); return; }
    setSaving(true);
    const success = await onSave({
      bereich_id: bereichId,
      typ,
      titel: titel.trim(),
      inhalt: inhalt.trim() || null,
      erstellt_von: 'admin',
    });
    setSaving(false);
    if (success) onCancel();
  };

  return (
    <div className="border border-blue-200 rounded-lg p-3 mb-3 bg-blue-50/30">
      <div className="flex items-center gap-2 mb-2">
        <input
          value={titel}
          onChange={(e) => setTitel(e.target.value)}
          placeholder="Titel..."
          className="flex-1 px-2 py-1.5 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-1 focus:ring-blue-300"
        />
        <select
          value={typ}
          onChange={(e) => setTyp(e.target.value)}
          className="px-2 py-1.5 text-xs border border-gray-200 rounded-lg bg-white"
        >
          <option value="notiz">Notiz</option>
          <option value="anweisung">Anweisung</option>
          <option value="frage">Frage</option>
          <option value="feedback">Feedback</option>
          <option value="bug">Bug</option>
          <option value="feature">Feature</option>
          <option value="architektur">Architektur</option>
          <option value="release">Release</option>
          <option value="regeln">Regeln</option>
        </select>
      </div>
      <textarea
        value={inhalt}
        onChange={(e) => setInhalt(e.target.value)}
        placeholder="Inhalt, Anweisungen, Details..."
        rows={4}
        className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-1 focus:ring-blue-300 resize-none"
      />
      <div className="flex items-center justify-end gap-2 mt-2">
        <button onClick={onCancel} className="px-3 py-1.5 text-xs text-gray-500 hover:text-gray-700 rounded-lg hover:bg-gray-100">
          Abbrechen
        </button>
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-3 py-1.5 text-xs text-white bg-[#9B1C1C] rounded-lg hover:bg-[#7F1D1D] disabled:opacity-50 flex items-center gap-1"
        >
          {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
          {editEintrag ? 'Aktualisieren' : 'Speichern'}
        </button>
      </div>
    </div>
  );
}

// ─── Zugangsdaten Bereich (eingebettet) ────────────────────────────────────

function ZugangsdatenBereich() {
  const { data, loading } = useZugangsdaten();
  const [sichtbar, setSichtbar] = useState<Record<string, boolean>>({});

  if (loading) return <div className="text-xs text-gray-400 py-4 text-center"><Loader2 className="w-4 h-4 animate-spin inline mr-1" /> Lade Zugangsdaten...</div>;

  const grouped = data.reduce((acc, z) => {
    if (!acc[z.kategorie]) acc[z.kategorie] = [];
    acc[z.kategorie].push(z);
    return acc;
  }, {} as Record<string, typeof data>);

  return (
    <div className="space-y-3">
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-2 text-[10px] text-yellow-700 flex items-center gap-1.5">
        <Shield className="w-3.5 h-3.5 flex-shrink-0" />
        RLS-geschützt. Nur für Admin sichtbar. Werte werden maskiert.
      </div>
      {Object.entries(grouped).map(([kat, items]) => (
        <div key={kat} className="border border-gray-200 rounded-lg p-3">
          <h4 className="text-xs font-bold text-foreground mb-2 capitalize">{kat}</h4>
          {items.map(z => (
            <div key={z.id} className="flex items-center gap-2 py-1.5 border-b border-gray-50 last:border-0">
              <span className="text-xs font-medium text-gray-700 w-20">{z.dienst}</span>
              <code className="text-[10px] bg-gray-100 px-1.5 py-0.5 rounded text-gray-500">{z.schluessel}</code>
              <span className="flex-1 text-xs font-mono text-gray-600">
                {sichtbar[z.id] ? z.wert : z.wert.substring(0, 8) + '•'.repeat(12)}
              </span>
              <button onClick={() => setSichtbar(p => ({ ...p, [z.id]: !p[z.id] }))} className="p-1 rounded hover:bg-gray-100">
                {sichtbar[z.id] ? <EyeOff className="w-3 h-3 text-gray-400" /> : <Eye className="w-3 h-3 text-gray-400" />}
              </button>
              <button onClick={() => { navigator.clipboard.writeText(z.wert); toast.success('Kopiert'); }} className="p-1 rounded hover:bg-gray-100">
                <Copy className="w-3 h-3 text-gray-400" />
              </button>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

// ─── Marken Bereich (eingebettet) ──────────────────────────────────────────

function MarkenBereich() {
  const { data, loading } = useMarkenConfig();

  if (loading) return <div className="text-xs text-gray-400 py-4 text-center"><Loader2 className="w-4 h-4 animate-spin inline mr-1" /> Lade Marken...</div>;

  return (
    <div className="space-y-2">
      {data.map(m => (
        <div key={m.id} className="border border-gray-200 rounded-lg p-3">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-6 h-6 rounded-md flex items-center justify-center text-[10px] font-bold text-white" style={{ backgroundColor: m.farbe_primaer || '#666' }}>
              {m.anzeigename.substring(0, 2).toUpperCase()}
            </div>
            <span className="text-sm font-medium">{m.anzeigename}</span>
            {m.ist_aktiv && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-green-100 text-green-600">aktiv</span>}
            {m.domain && <a href={`https://${m.domain}`} target="_blank" className="text-[10px] text-blue-500 hover:underline">{m.domain}</a>}
          </div>
          {m.beschreibung && <p className="text-[10px] text-gray-400 ml-8">{m.beschreibung}</p>}
          {m.ki_anweisungen && (
            <div className="mt-2 ml-8 bg-purple-50 border border-purple-100 rounded p-2">
              <div className="text-[9px] font-medium text-purple-600 mb-0.5 flex items-center gap-1"><Cpu className="w-2.5 h-2.5" /> KI-Anweisungen</div>
              <p className="text-[10px] text-purple-700 whitespace-pre-wrap">{m.ki_anweisungen.substring(0, 300)}{m.ki_anweisungen.length > 300 ? '...' : ''}</p>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ─── Fragen & Feedback Bereich ─────────────────────────────────────────────

function FragenBereich({ bereichId, eintraege, onCreate, onUpdate, onDelete }: {
  bereichId: string;
  eintraege: KiCockpitEintrag[];
  onCreate: (data: any) => Promise<boolean>;
  onUpdate: (id: string, data: any) => Promise<boolean>;
  onDelete: (id: string) => Promise<boolean>;
}) {
  const [showForm, setShowForm] = useState(false);
  const [antwort, setAntwort] = useState<Record<string, string>>({});

  const offeneFragenVonKi = eintraege.filter(e => e.erstellt_von === 'ki_agent' && e.typ === 'frage');
  const meineNotizen = eintraege.filter(e => e.erstellt_von === 'admin');

  const handleAntwort = async (frageId: string) => {
    if (!antwort[frageId]?.trim()) return;
    await onCreate({
      bereich_id: bereichId,
      typ: 'antwort',
      titel: `Antwort auf Frage`,
      inhalt: antwort[frageId].trim(),
      meta: { bezug_auf: frageId },
      erstellt_von: 'admin',
    });
    setAntwort(p => ({ ...p, [frageId]: '' }));
  };

  return (
    <div className="space-y-4">
      {offeneFragenVonKi.length > 0 && (
        <div>
          <h4 className="text-xs font-bold text-orange-600 mb-2 flex items-center gap-1">
            <AlertCircle className="w-3.5 h-3.5" /> Offene Rückfragen ({offeneFragenVonKi.length})
          </h4>
          {offeneFragenVonKi.map(f => (
            <div key={f.id} className="border border-orange-200 rounded-lg p-3 mb-2 bg-orange-50/30">
              <div className="flex items-center gap-2 mb-1">
                <Cpu className="w-3.5 h-3.5 text-purple-500" />
                <span className="text-sm font-medium">{f.titel}</span>
                <span className="text-[10px] text-gray-400">{new Date(f.erstellt_am).toLocaleDateString('de-DE')}</span>
              </div>
              {f.inhalt && <p className="text-xs text-gray-600 ml-5 mb-2 whitespace-pre-wrap">{f.inhalt}</p>}
              <div className="ml-5 flex items-center gap-2">
                <input
                  value={antwort[f.id] || ''}
                  onChange={(e) => setAntwort(p => ({ ...p, [f.id]: e.target.value }))}
                  placeholder="Deine Antwort..."
                  className="flex-1 px-2 py-1.5 text-xs border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-1 focus:ring-orange-300"
                  onKeyDown={(e) => { if (e.key === 'Enter') handleAntwort(f.id); }}
                />
                <button
                  onClick={() => handleAntwort(f.id)}
                  className="px-2 py-1.5 text-xs text-white bg-orange-500 rounded-lg hover:bg-orange-600 flex items-center gap-1"
                >
                  <Send className="w-3 h-3" /> Antworten
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div>
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-xs font-bold text-foreground">Deine Notizen & Anweisungen</h4>
          <button onClick={() => setShowForm(true)} className="text-[10px] text-[#9B1C1C] hover:underline flex items-center gap-0.5">
            <Plus className="w-3 h-3" /> Neue Notiz
          </button>
        </div>
        {showForm && (
          <NeuerEintragForm
            bereichId={bereichId}
            onSave={onCreate}
            onCancel={() => setShowForm(false)}
          />
        )}
        {meineNotizen.map(n => (
          <EintragCard key={n.id} eintrag={n} onEdit={() => {}} onDelete={onDelete} />
        ))}
        {meineNotizen.length === 0 && !showForm && (
          <p className="text-xs text-gray-400 py-3 text-center">Noch keine Notizen. Schreibe Anweisungen oder Hinweise.</p>
        )}
      </div>
    </div>
  );
}

// ─── Hauptkomponente ───────────────────────────────────────────────────────

export default function KiCockpitSection() {
  const { bereiche, eintraege, loading, error, getEintraege, createEintrag, updateEintrag, deleteEintrag } = useKiCockpit();
  const [activeTab, setActiveTab] = useState<CockpitBereich>('briefing');
  const [showForm, setShowForm] = useState(false);
  const [editEintrag, setEditEintrag] = useState<KiCockpitEintrag | null>(null);

  const activeBereich = bereiche.find(b => b.bereich === activeTab);
  const activeEintraege = activeBereich ? getEintraege(activeBereich.id) : [];

  // Zähler für Badges
  const counts = useMemo(() => {
    const c: Record<string, number> = {};
    bereiche.forEach(b => {
      c[b.bereich] = eintraege.filter(e => e.bereich_id === b.id && e.status !== 'archiviert').length;
    });
    return c;
  }, [bereiche, eintraege]);

  // Offene KI-Fragen zählen
  const offeneFragenCount = useMemo(() => {
    const fragenBereich = bereiche.find(b => b.bereich === 'fragen');
    if (!fragenBereich) return 0;
    return eintraege.filter(e => e.bereich_id === fragenBereich.id && e.erstellt_von === 'ki_agent' && e.typ === 'frage').length;
  }, [bereiche, eintraege]);

  const handleSave = async (data: any) => {
    if (editEintrag) {
      return updateEintrag(editEintrag.id, data);
    }
    return createEintrag(data);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-5 h-5 animate-spin text-gray-400 mr-2" />
        <span className="text-sm text-gray-400">KI-Cockpit wird geladen...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-600">
        <AlertCircle className="w-4 h-4 inline mr-1" /> Fehler: {error}
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
          <Cpu className="w-5 h-5 text-purple-600" />
        </div>
        <div>
          <h2 className="text-base font-bold text-foreground">KI-Cockpit</h2>
          <p className="text-[10px] text-gray-400">Zentrales Briefing, Roadmap, Notizen und Feedback-System</p>
        </div>
        {offeneFragenCount > 0 && (
          <button
            onClick={() => setActiveTab('fragen')}
            className="ml-auto px-3 py-1.5 text-xs bg-orange-100 text-orange-600 rounded-lg hover:bg-orange-200 flex items-center gap-1"
          >
            <AlertCircle className="w-3.5 h-3.5" />
            {offeneFragenCount} offene Frage{offeneFragenCount > 1 ? 'n' : ''}
          </button>
        )}
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-1 mb-4 overflow-x-auto pb-1 border-b border-gray-100">
        {bereiche.filter(b => b.ist_aktiv).map(b => {
          const config = BEREICH_CONFIG[b.bereich as CockpitBereich];
          if (!config) return null;
          const Icon = config.icon;
          const isActive = activeTab === b.bereich;
          const count = counts[b.bereich] || 0;
          return (
            <button
              key={b.id}
              onClick={() => setActiveTab(b.bereich as CockpitBereich)}
              className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-t-lg whitespace-nowrap transition-colors border-b-2 ${
                isActive
                  ? `${config.farbe} border-current ${config.bgFarbe}`
                  : 'text-gray-400 border-transparent hover:text-gray-600 hover:bg-gray-50'
              }`}
            >
              <Icon className="w-3.5 h-3.5" />
              {b.titel}
              {count > 0 && (
                <span className={`text-[9px] px-1 py-0 rounded-full ${isActive ? 'bg-white/50' : 'bg-gray-100'}`}>
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      <div className="min-h-[300px]">
        {/* Spezial-Bereiche */}
        {activeTab === 'zugangsdaten' && <ZugangsdatenBereich />}
        {activeTab === 'marken' && <MarkenBereich />}
        {activeTab === 'fragen' && activeBereich && (
          <FragenBereich
            bereichId={activeBereich.id}
            eintraege={activeEintraege}
            onCreate={createEintrag}
            onUpdate={updateEintrag}
            onDelete={deleteEintrag}
          />
        )}

        {/* Standard-Bereiche (Briefing, Roadmap, Changelog, Datenbank, Notizen) */}
        {!['zugangsdaten', 'marken', 'fragen'].includes(activeTab) && (
          <div>
            {/* Toolbar */}
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs text-gray-400">
                {activeBereich?.beschreibung || ''}
              </p>
              <button
                onClick={() => { setEditEintrag(null); setShowForm(true); }}
                className="px-3 py-1.5 text-xs text-white bg-[#9B1C1C] rounded-lg hover:bg-[#7F1D1D] flex items-center gap-1"
              >
                <Plus className="w-3 h-3" /> Neuer Eintrag
              </button>
            </div>

            {/* Neuer Eintrag Form */}
            {showForm && activeBereich && (
              <NeuerEintragForm
                bereichId={activeBereich.id}
                onSave={handleSave}
                onCancel={() => { setShowForm(false); setEditEintrag(null); }}
                editEintrag={editEintrag}
              />
            )}

            {/* Einträge */}
            {activeEintraege.length === 0 && !showForm ? (
              <div className="text-center py-8">
                <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-2">
                  {BEREICH_CONFIG[activeTab] && (() => {
                    const Icon = BEREICH_CONFIG[activeTab].icon;
                    return <Icon className="w-5 h-5 text-gray-400" />;
                  })()}
                </div>
                <p className="text-sm text-gray-400">Noch keine Einträge</p>
                <p className="text-xs text-gray-300 mt-0.5">Erstelle den ersten Eintrag für diesen Bereich</p>
              </div>
            ) : (
              <div>
                {activeEintraege.map(e => (
                  <EintragCard
                    key={e.id}
                    eintrag={e}
                    onEdit={(entry) => { setEditEintrag(entry); setShowForm(true); }}
                    onDelete={deleteEintrag}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
